(function () {
  'use strict';

  /* ------------------------------------------------------------------
   * Federica Oliva — immersione modelle.
   *
   * Desktop: navigazione a STEP (un gesto = una scena).
   * Mobile: hero statico, scroll nativo. Lo swipe a step bloccava la pagina
   * e faceva abbandonare lo scroll.
   * ------------------------------------------------------------------ */

  var MOBILE_QUERY = '(max-width: 900px)';

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  function waitGsap(cb, fail, n) {
    n = n || 0;
    if (window.gsap) return cb();
    if (n > 120) return fail && fail();
    setTimeout(function () {
      waitGsap(cb, fail, n + 1);
    }, 40);
  }

  /* Se GSAP non arriva (rete che blocca i CDN, script bloccati) la sezione
     mostrerebbe tutte le figure sovrapposte: meglio la sola silhouette. */
  function staticFallback(section) {
    var p = parts(section);
    section.classList.add('is-ready', 'is-static');
    [p.left1, p.right1, p.left2, p.right2, p.brand].forEach(function (el) {
      if (el) el.style.display = 'none';
    });
    [p.hero, p.copy].forEach(function (el) {
      if (!el) return;
      el.style.visibility = 'visible';
      el.style.opacity = '1';
    });
    if (p.hint) p.hint.style.display = 'none';
  }

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function introActive() {
    return document.documentElement.classList.contains('fo-intro-on');
  }

  /* Su smartphone la barra visibile è .site-control (fixed), mentre .page-header
     può collassare a pochi pixel: prendendo il primo selettore che risponde si
     otteneva 72px invece di 92 e il contenuto finiva sotto al logo. */
  function measureHeader(section) {
    var sels = [
      '#shopify-section-header .site-control',
      '#shopify-section-header .page-header',
      '.section-header .page-header',
      '#shopify-section-header'
    ];
    var h = 0;
    for (var i = 0; i < sels.length; i++) {
      var node = document.querySelector(sels[i]);
      if (!node) continue;
      var nh = Math.ceil(node.getBoundingClientRect().height);
      if (nh > h) h = nh;
    }
    if (h < 56) h = 72;
    if (h > 140) h = 110;
    section.style.setProperty('--fo-immersive-header', h + 8 + 'px');
    return h;
  }

  function inner(el) {
    return el ? el.querySelector('[data-fo-anim]') || el : null;
  }

  function parts(section) {
    return {
      sticky: section.querySelector('[data-fo-sticky]') || section.querySelector('.fo-immersive__sticky'),
      hero: section.querySelector('[data-role="hero"]'),
      left1: section.querySelector('[data-role="left-1"]'),
      right1: section.querySelector('[data-role="right-1"]'),
      left2: section.querySelector('[data-role="left-2"]'),
      right2: section.querySelector('[data-role="right-2"]'),
      brand: section.querySelector('[data-fo-brand]'),
      copy: section.querySelector('[data-fo-copy]'),
      hint: section.querySelector('[data-fo-hint]'),
      eyebrow: section.querySelector('.fo-immersive__eyebrow'),
      progress: section.querySelector('[data-fo-progress-bar]'),
      labels: section.querySelectorAll('[data-fo-scene-label]')
    };
  }

  /* =================================================================
   * DESKTOP — scene a scatti (comportamento originale)
   * ================================================================= */

  function initDesktop(section) {
    var gsap = window.gsap;
    var p = parts(section);
    if (!p.hero) return null;

    var headerH = measureHeader(section);

    var current = 0;
    var scenes = 3;
    var locked = true;
    var touchY = 0;
    var activeTween = null;
    var cooldownUntil = 0;

    function setPinning(on) {
      document.body.classList.toggle('fo-immersive-pinning', on);
      section.classList.toggle('is-locked', on);
    }

    function updateHeaderForScroll() {
      var rect = section.getBoundingClientRect();
      document.body.classList.toggle('fo-immersive-over', rect.bottom > headerH + 8);
    }

    var heroI = inner(p.hero);
    var left1I = inner(p.left1);
    var right1I = inner(p.right1);
    var left2I = inner(p.left2);
    var right2I = inner(p.right2);
    var brandI = inner(p.brand);
    var copy = p.copy;
    var hint = p.hint;
    var sticky = p.sticky;

    [p.hero, p.left1, p.right1, p.left2, p.right2, p.brand, copy].forEach(function (el) {
      if (!el) return;
      el.style.visibility = 'visible';
      el.style.opacity = '1';
      el.style.pointerEvents = el === copy ? 'auto' : 'none';
    });

    gsap.set(heroI, { autoAlpha: 1, scale: 1, y: 0 });
    gsap.set(left1I, { autoAlpha: 0, x: -56 });
    gsap.set(right1I, { autoAlpha: 0, x: 56 });
    gsap.set(left2I, { autoAlpha: 0, x: -40 });
    gsap.set(right2I, { autoAlpha: 0, x: 40 });
    gsap.set(brandI, { autoAlpha: 0, scale: 0.94, y: 24 });
    gsap.set(copy, { autoAlpha: 0, y: 16 });
    if (hint) gsap.set(hint, { autoAlpha: 1 });
    if (sticky) gsap.set(sticky, { autoAlpha: 1 });

    function targetsFor(index) {
      if (index === 0) {
        return {
          hero: { autoAlpha: 1, scale: 1, y: 0 },
          left1: { autoAlpha: 0, x: -56 },
          right1: { autoAlpha: 0, x: 56 },
          left2: { autoAlpha: 0, x: -40 },
          right2: { autoAlpha: 0, x: 40 },
          brand: { autoAlpha: 0, scale: 0.94, y: 24 },
          copy: { autoAlpha: 0, y: 16 },
          hint: { autoAlpha: 1 }
        };
      }
      if (index === 1) {
        return {
          hero: { autoAlpha: 0, scale: 0.96, y: -10 },
          left1: { autoAlpha: 1, x: 0 },
          right1: { autoAlpha: 1, x: 0 },
          left2: { autoAlpha: 0, x: -36 },
          right2: { autoAlpha: 0, x: 36 },
          brand: { autoAlpha: 1, scale: 1, y: 0 },
          copy: { autoAlpha: 0, y: 12 },
          hint: { autoAlpha: 0 }
        };
      }
      return {
        hero: { autoAlpha: 0, scale: 0.94, y: -10 },
        left1: { autoAlpha: 1, x: 0 },
        right1: { autoAlpha: 1, x: 0 },
        left2: { autoAlpha: 1, x: 0 },
        right2: { autoAlpha: 1, x: 0 },
        brand: { autoAlpha: 1, scale: 1, y: 0 },
        copy: { autoAlpha: 1, y: 0 },
        hint: { autoAlpha: 0 }
      };
    }

    function goTo(index) {
      if (index < 0) return;
      if (index >= scenes) return;
      /* Durante un'animazione ignora altri input: altrimenti una raffica di
         wheel/touch salta direttamente all'ultimo step. */
      if (activeTween) return;
      if (index === current) return;

      current = index;
      section.classList.remove('scene-1', 'scene-2', 'scene-3');
      section.classList.add('scene-' + (current + 1));

      var t = targetsFor(current);

      activeTween = gsap.timeline({
        defaults: { duration: 1.05, ease: 'power3.inOut' },
        onComplete: function () {
          activeTween = null;
          cooldownUntil = Date.now() + 180;
        }
      });

      if (heroI) activeTween.to(heroI, t.hero, 0);
      if (left1I) activeTween.to(left1I, t.left1, 0);
      if (right1I) activeTween.to(right1I, t.right1, 0);
      if (left2I) activeTween.to(left2I, t.left2, 0);
      if (right2I) activeTween.to(right2I, t.right2, 0);
      if (brandI) activeTween.to(brandI, t.brand, 0);
      if (copy) activeTween.to(copy, t.copy, 0);
      if (hint) activeTween.to(hint, Object.assign({ duration: 0.55 }, t.hint), 0);
    }

    /* Un gesto (raffica di wheel o uno swipe) = uno step, poi bisogna
       sollevare il dito / fermare la rotella prima del successivo. */
    var gestureArmed = true;
    var gestureTimer = null;

    function armGestureLater() {
      gestureArmed = false;
      if (gestureTimer) window.clearTimeout(gestureTimer);
      gestureTimer = window.setTimeout(function () {
        gestureArmed = true;
      }, 280);
    }

    function canStep() {
      return !activeTween && gestureArmed && Date.now() >= cooldownUntil;
    }

    function unlock() {
      if (!locked) return;
      locked = false;
      section.classList.add('is-complete');
      document.documentElement.classList.remove('fo-immersive-noscroll');
      setPinning(false);
      document.body.classList.add('fo-immersive-over');
      /* da qui in poi la barra può diventare opaca: sopra le sezioni
         successive il logo trasparente non si leggerebbe */
      document.body.classList.add('fo-immersive-done');
      updateHeaderForScroll();
      document.dispatchEvent(new CustomEvent('fo:immersive:done'));
    }

    function relock() {
      if (locked) return;
      locked = true;
      section.classList.remove('is-complete');
      document.body.classList.remove('fo-immersive-done');
      document.documentElement.classList.add('fo-immersive-noscroll');
      setPinning(true);
      document.body.classList.add('fo-immersive-over');
      window.scrollTo(0, section.offsetTop);
      if (activeTween) {
        activeTween.kill();
        activeTween = null;
      }
      current = scenes - 1;
      section.classList.remove('scene-1', 'scene-2', 'scene-3');
      section.classList.add('scene-3');
      var t = targetsFor(2);
      gsap.set(heroI, t.hero);
      if (left1I) gsap.set(left1I, t.left1);
      if (right1I) gsap.set(right1I, t.right1);
      if (left2I) gsap.set(left2I, t.left2);
      if (right2I) gsap.set(right2I, t.right2);
      if (brandI) gsap.set(brandI, t.brand);
      if (copy) gsap.set(copy, t.copy);
      if (hint) gsap.set(hint, t.hint);
      if (sticky) gsap.set(sticky, { autoAlpha: 1 });
      armGestureLater();
    }

    function onWheel(e) {
      if (introActive()) {
        if (e.cancelable) e.preventDefault();
        return;
      }
      if (!locked) {
        var rect = section.getBoundingClientRect();
        if (e.deltaY < 0 && rect.top >= -4 && rect.top < headerH + 40 && window.pageYOffset <= section.offsetTop + 8) {
          e.preventDefault();
          relock();
          return;
        }
        return;
      }

      e.preventDefault();

      if (!canStep()) return;

      if (current >= scenes - 1 && e.deltaY > 10) {
        armGestureLater();
        unlock();
        return;
      }

      if (e.deltaY > 10) {
        armGestureLater();
        goTo(current + 1);
      } else if (e.deltaY < -10) {
        armGestureLater();
        goTo(current - 1);
      }
    }

    function onTouchStart(e) {
      if (!e.touches || !e.touches.length) return;
      touchY = e.touches[0].clientY;
    }

    function onTouchEnd() {
      /* Non riarmare durante il cooldown post-intro: altrimenti il touchend
         dello skip/tocco cancella il lock e lo step salta avanti. */
      if (introActive() || Date.now() < cooldownUntil) return;
      gestureArmed = true;
      if (gestureTimer) window.clearTimeout(gestureTimer);
    }

    function onTouchMove(e) {
      if (introActive()) {
        if (e.cancelable) e.preventDefault();
        return;
      }
      if (!locked) {
        var rect = section.getBoundingClientRect();
        if (!e.touches || !e.touches.length) return;
        var y = e.touches[0].clientY;
        var dy = touchY - y;
        if (dy < -50 && rect.top >= -4 && window.pageYOffset <= section.offsetTop + 8) {
          e.preventDefault();
          relock();
        }
        touchY = y;
        return;
      }

      if (!e.touches || !e.touches.length) return;

      var y2 = e.touches[0].clientY;
      var dy2 = touchY - y2;

      e.preventDefault();
      if (!canStep()) return;
      if (Math.abs(dy2) < 48) return;

      touchY = y2;
      gestureArmed = false;

      if (current >= scenes - 1 && dy2 > 0) {
        unlock();
        return;
      }

      if (dy2 > 0) goTo(current + 1);
      else goTo(current - 1);
    }

    function onKey(e) {
      if (introActive()) return;
      if (!locked) return;
      if (activeTween) return;
      if (e.key === 'ArrowDown' || e.key === 'PageDown' || e.key === ' ') {
        e.preventDefault();
        if (current >= scenes - 1) unlock();
        else goTo(current + 1);
      } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
        e.preventDefault();
        goTo(current - 1);
      }
    }

    function onResize() {
      headerH = measureHeader(section);
      updateHeaderForScroll();
    }

    window.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    window.addEventListener('touchcancel', onTouchEnd, { passive: true });
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', updateHeaderForScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });

    document.documentElement.classList.add('fo-immersive-noscroll');
    setPinning(true);
    document.body.classList.add('fo-immersive-over');
    section.classList.add('is-ready', 'scene-1');
    current = 0;
    updateHeaderForScroll();

    /* Dopo l'intro: resta sullo step 0 (silhouette). L'inerzia di trackpad/dito
       non deve consumare subito uno o più step. */
    function resetToFirstStep() {
      if (activeTween) {
        activeTween.kill();
        activeTween = null;
      }
      current = 0;
      section.classList.remove('scene-1', 'scene-2', 'scene-3');
      section.classList.add('scene-1');
      var t0 = targetsFor(0);
      gsap.set(heroI, t0.hero);
      if (left1I) gsap.set(left1I, t0.left1);
      if (right1I) gsap.set(right1I, t0.right1);
      if (left2I) gsap.set(left2I, t0.left2);
      if (right2I) gsap.set(right2I, t0.right2);
      if (brandI) gsap.set(brandI, t0.brand);
      if (copy) gsap.set(copy, t0.copy);
      if (hint) gsap.set(hint, t0.hint);
      cooldownUntil = Date.now() + 1400;
      gestureArmed = false;
      if (gestureTimer) window.clearTimeout(gestureTimer);
      gestureTimer = window.setTimeout(function () {
        gestureArmed = true;
      }, 1400);
    }

    if (introActive()) {
      if (hint) gsap.set(hint, { autoAlpha: 0 });
      document.addEventListener('fo:intro:done', resetToFirstStep, { once: true });
      document.addEventListener(
        'fo:intro:ending',
        function () {
          resetToFirstStep();
          if (hint) {
            gsap.to(hint, { autoAlpha: 1, duration: 1.1, delay: 0.9, ease: 'power2.out' });
          }
        },
        { once: true }
      );
    }

    return function destroy() {
      window.removeEventListener('wheel', onWheel, { passive: false });
      window.removeEventListener('touchstart', onTouchStart, { passive: true });
      window.removeEventListener('touchmove', onTouchMove, { passive: false });
      window.removeEventListener('touchend', onTouchEnd, { passive: true });
      window.removeEventListener('touchcancel', onTouchEnd, { passive: true });
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', updateHeaderForScroll, { passive: true });
      window.removeEventListener('resize', onResize, { passive: true });
      if (gestureTimer) window.clearTimeout(gestureTimer);
      if (activeTween) activeTween.kill();
      document.documentElement.classList.remove('fo-immersive-noscroll');
      setPinning(false);
      section.classList.remove('is-ready', 'is-complete', 'scene-1', 'scene-2', 'scene-3');
      gsap.set(
        [heroI, left1I, right1I, left2I, right2I, brandI, copy, hint].filter(Boolean),
        { clearProps: 'all' }
      );
    };
  }

  /* =================================================================
   * MOBILE — hero statico, scroll nativo (niente lock / step)
   * ================================================================= */

  function initMobile(section) {
    var gsap = window.gsap;
    var p = parts(section);
    if (!p.hero) return null;

    var headerH = measureHeader(section);
    var heroI = inner(p.hero);
    var copy = p.copy;
    var hint = p.hint;
    var labels = Array.prototype.slice.call(p.labels || []);
    var fadeTween = null;

    section.classList.add('is-mobile', 'is-ready', 'is-static');
    section.style.setProperty('--fo-mobile-screens', '1');

    [p.left1, p.right1, p.left2, p.right2, p.brand].forEach(function (el) {
      if (el) el.style.display = 'none';
    });
    [p.hero, copy].forEach(function (el) {
      if (!el) return;
      el.style.visibility = 'visible';
      el.style.opacity = '1';
      el.style.pointerEvents = el === copy ? 'auto' : 'none';
    });
    if (hint) hint.style.display = 'none';
    if (p.progress) p.progress.style.display = 'none';
    labels.forEach(function (el) {
      el.style.display = 'none';
    });

    function updateHeader() {
      var rect = section.getBoundingClientRect();
      document.body.classList.toggle('fo-immersive-over', rect.bottom > headerH + 8);
    }

    function measureOffset() {
      var top = Math.round(section.getBoundingClientRect().top + (window.scrollY || 0));
      if (top < 0) top = 0;
      if (top > 200) top = 200;
      section.style.setProperty('--fo-mobile-offset', top + 'px');
    }

    function measureCopy() {
      if (!copy) return;
      measureOffset();
      var h = Math.ceil(copy.getBoundingClientRect().height);
      if (h < 120) h = 120;
      var cap = Math.min(340, Math.round((window.innerHeight || 700) * 0.4));
      if (h > cap) h = cap;
      section.style.setProperty('--fo-mobile-copy', h + 'px');
    }

    function remeasureLater() {
      window.requestAnimationFrame(measureCopy);
      window.setTimeout(measureCopy, 400);
      window.setTimeout(measureCopy, 1200);
      if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
        document.fonts.ready.then(measureCopy).catch(function () {});
      }
    }

    measureCopy();
    remeasureLater();

    if (heroI) gsap.set(heroI, { autoAlpha: 1, scale: 1, y: 0, yPercent: 0, xPercent: 0 });

    function revealCopy() {
      if (!copy) return;
      fadeTween = gsap.fromTo(
        copy,
        { autoAlpha: 0, y: 14 },
        { autoAlpha: 1, y: 0, duration: 0.9, ease: 'power2.out' }
      );
    }

    if (introActive()) {
      if (copy) gsap.set(copy, { autoAlpha: 0, y: 14 });
    } else if (copy) {
      gsap.set(copy, { autoAlpha: 1, y: 0 });
    }

    function onResize() {
      headerH = measureHeader(section);
      measureCopy();
      updateHeader();
    }

    window.addEventListener('scroll', updateHeader, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
    window.addEventListener('orientationchange', onResize, { passive: true });

    document.body.classList.add('fo-immersive-done');
    document.documentElement.classList.remove('fo-immersive-noscroll');
    document.body.classList.remove('fo-immersive-pinning');
    section.classList.remove('is-locked');
    updateHeader();
    document.dispatchEvent(new CustomEvent('fo:immersive:done'));

    document.addEventListener('fo:intro:ending', revealCopy, { once: true });
    document.addEventListener('fo:intro:done', function () {
      measureCopy();
    }, { once: true });

    return function destroy() {
      window.removeEventListener('scroll', updateHeader, { passive: true });
      window.removeEventListener('resize', onResize, { passive: true });
      window.removeEventListener('orientationchange', onResize, { passive: true });
      if (fadeTween) fadeTween.kill();
      document.documentElement.classList.remove('fo-immersive-noscroll');
      document.body.classList.remove('fo-immersive-pinning');
      section.classList.remove('is-mobile', 'is-ready', 'is-static', 'is-steps', 'is-locked', 'is-complete');
      section.style.removeProperty('--fo-mobile-screens');
      gsap.set([heroI, copy, hint].filter(Boolean), { clearProps: 'all' });
    };
  }

  /* =================================================================
   * Bootstrap + cambio modalità al ruotare del device
   * ================================================================= */

  ready(function () {
    var section = document.querySelector('[data-fo-immersive]');
    if (!section) return;

    waitGsap(function () {
      var mq = window.matchMedia(MOBILE_QUERY);
      var destroy = null;
      var mode = null;

      function mount() {
        var next = mq.matches ? 'mobile' : 'desktop';
        if (next === mode) return;
        if (destroy) destroy();
        mode = next;
        destroy = next === 'mobile' ? initMobile(section) : initDesktop(section);
      }

      mount();

      if (typeof mq.addEventListener === 'function') {
        mq.addEventListener('change', mount);
      } else if (typeof mq.addListener === 'function') {
        mq.addListener(mount);
      }
    }, function () {
      staticFallback(section);
    });
  });
})();
